import { numberCaptions } from '@confinion/confideck-shared';
import type { Node as PMNode, Schema } from '@tiptap/pm/model';

import { collectCaptionPatches } from '../../tiptap/nodes/caption/plugins/getAutoIndexCaptionsPlugin';
import {
  collectCitationOccurrences,
  collectLiteratureCitationPatches,
} from '../../tiptap/nodes/literatureCitation/plugins/getAutoIndexLiteratureCitationsPlugin';
import type { TElement } from './elements';
import { applyPatches, parseWholeDoc, stateOver, type TPatched } from './wholeDocument';

/**
 * Caption and citation indices are persisted as `data-index` and are whole-document properties, so
 * a change inside the live part shifts numbers in elements the editor does not hold. Called on commit,
 * never on keystroke; only elements whose numbering changed are re-serialized.
 */
export const renumberElements = (schema: Schema, elements: readonly TElement[], prepared?: PMNode): TPatched => {
  if (!elements.length) return { elements: elements as TElement[], doc: prepared ?? parseWholeDoc(schema, elements) };
  // `prepared` is the same document assembled without parsing - see `commitFromEditor`.
  const doc = prepared ?? parseWholeDoc(schema, elements);
  const state = stateOver(doc);
  const patches = [...collectCaptionPatches(state), ...collectLiteratureCitationPatches(state)];
  // `applyPatches` hands back the doc its result was written from, so the caller never has to parse
  // the whole document again straight after this - see `commitFromEditor`.
  return applyPatches(schema, elements, doc, patches);
};

export type TCitationSeed = { literatureId: string | null; currentIndex: number | null };

export type TNumberingSeed = {
  figure: number;
  table: number;
  citationsBefore: TCitationSeed[];
  citationsAfter: TCitationSeed[];
};

const toSeed = (occurrence: { literatureId: string; currentIndex: number | null }): TCitationSeed => ({
  literatureId: occurrence.literatureId || null,
  currentIndex: occurrence.currentIndex,
});

/**
 * What the editor needs to continue the document's counters instead of restarting at 1. Citations
 * need what FOLLOWS the part too: a repeated citation reuses the number of its first occurrence,
 * which may be below the live part.
 */
export const numberingSeed = (doc: PMNode, sliceFrom: number, sliceTo: number): TNumberingSeed => {
  const before = doc.cut(0, sliceFrom);
  const after = doc.cut(sliceTo);
  const captions = numberCaptions(before);
  return {
    figure: captions.filter(({ kind }) => kind === 'FIGURE').length,
    table: captions.filter(({ kind }) => kind === 'TABLE').length,
    citationsBefore: collectCitationOccurrences(before).map(toSeed),
    citationsAfter: collectCitationOccurrences(after).map(toSeed),
  };
};
